import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  Timestamp,
  updateDoc,
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import type { Formulation, Measurement, MeasurementKey, Source } from '../lib/types';
import { MEASUREMENT_FIELDS, MEASUREMENT_KEYS } from '../lib/types';
import { touchProject } from './projects';

export const formulationsCol = (projectId: string) =>
  collection(db, 'projects', projectId, 'formulations');

export const formulationDoc = (projectId: string, id: string) =>
  doc(db, 'projects', projectId, 'formulations', id);

export function watchFormulations(
  projectId: string,
  cb: (f: Formulation[]) => void,
  onError?: (e: Error) => void,
) {
  const q = query(formulationsCol(projectId), orderBy('createdAt', 'asc'));
  return onSnapshot(
    q,
    (snap) => cb(snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Formulation)),
    (e) => onError?.(e),
  );
}

export type FormulationDraft = {
  label: string;
  lipid: string;
  np: number | null;
  modification: string;
  measuredAt: Date | null;
  notes: string;
};

export function emptyDraft(): FormulationDraft {
  return { label: '', lipid: '', np: null, modification: '', measuredAt: new Date(), notes: '' };
}

export async function createFormulation(projectId: string, draft: FormulationDraft) {
  const ref = await addDoc(formulationsCol(projectId), {
    label: draft.label.trim(),
    lipid: draft.lipid.trim(),
    np: draft.np,
    modification: draft.modification.trim(),
    measuredAt: draft.measuredAt ? Timestamp.fromDate(draft.measuredAt) : null,
    size: null,
    pdi: null,
    zeta: null,
    ee: null,
    notes: draft.notes.trim(),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });
  await touchProject(projectId, 1);
  return ref.id;
}

export async function updateFormulationMeta(
  projectId: string,
  id: string,
  draft: FormulationDraft,
) {
  await updateDoc(formulationDoc(projectId, id), {
    label: draft.label.trim(),
    lipid: draft.lipid.trim(),
    np: draft.np,
    modification: draft.modification.trim(),
    measuredAt: draft.measuredAt ? Timestamp.fromDate(draft.measuredAt) : null,
    notes: draft.notes.trim(),
    updatedAt: serverTimestamp(),
  });
  await touchProject(projectId);
}

export async function deleteFormulation(projectId: string, id: string) {
  await deleteDoc(formulationDoc(projectId, id));
  await touchProject(projectId, -1);
}

export function buildMeasurement(
  key: MeasurementKey,
  value: number,
  sd: number | null,
  source: Source,
  captureId: string | null,
): Measurement {
  return { value, sd, unit: MEASUREMENT_FIELDS[key].unit, source, captureId };
}

/**
 * Write measurements onto a formulation.
 * A key mapped to null clears that field; a key left out of the patch is
 * untouched. That distinction is what lets one capture fill size and PDI
 * while leaving a hand-typed zeta from an earlier retake alone.
 */
export async function applyMeasurements(
  projectId: string,
  id: string,
  patch: Partial<Record<MeasurementKey, Measurement | null>>,
) {
  const update: Record<string, Measurement | null | ReturnType<typeof serverTimestamp>> = {
    updatedAt: serverTimestamp(),
  };
  for (const key of MEASUREMENT_KEYS) {
    if (key in patch) update[key] = patch[key] ?? null;
  }
  await updateDoc(formulationDoc(projectId, id), update);
  await touchProject(projectId);
}
